import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCSwitch, RCCServicesApi } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import Tabs from "src/components/Tabs";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import stringToNumber from "src/utils/stringToNumber";
import MetricsRanges from "src/components/MetricsRanges";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { IMetric, IComponentMetricModals, IRefMetricModals, IMetricForm } from "./interface";
import addAndChangeSchema from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefMetricModals, IComponentMetricModals> = ({
	paginationRef
}, ref) => {
	const { translate,currentLanguage} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [selectedMetric, setSelectedMetric] = useState<IMetric>({} as IMetric);

	const controllerUrl = "Metrics/";

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
	
	const actionButtonDelete = (row: IMetric) => {
		confirm(
			translate("Exclusão da métrica"),
			`${translate("Deseja realmente excluir métrica")}: ${selectedMetric.taa006Titulo} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: `${controllerUrl}Delete`,
					urlParameters: {
						id: row.taa006Id
					}
				});
		
				addToast({
					color: "green",
					description: translate("Métrica excluída com sucesso"),
					title: translate("Sucesso")
				});
					
				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir métrica"), addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		control:controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange 
	} = useForm<IMetricForm>();

	const { 
		fields, 
		remove,
		append 
	} = useFieldArray({
		control: controlAddOrChange,
		name: "tatba007"
	});

	const submitAddOrChange = async (data: IMetricForm) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			await addAndChangeSchema(translate,currentLanguage).validate(data, {
				abortEarly: false
			});

			const modificatedData = {...data};

			modificatedData.tatba007.forEach((part, index) => {
				if (typeof part.taa007Valorini === "string")
					modificatedData.tatba007[index].taa007Valorini = stringToNumber(part.taa007Valorini,currentLanguage) || 0;
				if (typeof part.taa007Valorfim === "string")
					modificatedData.tatba007[index].taa007Valorfim = stringToNumber(part.taa007Valorfim,currentLanguage) || 0;
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: `${controllerUrl}Insert`,
					body: {
						...modificatedData,
						taa006Situacao: modificatedData.taa006Situacao ? "A" : "I"
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: `${controllerUrl}Update`,
					body: {
						...modificatedData,
						taa006Id:selectedMetric.taa006Id,
						taa006Situacao: modificatedData.taa006Situacao ? "A" : "I"
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Métrica adicionada com sucesso") : translate("Métrica alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar métrica") : translate("Erro ao alterar métrica"), addToast);
		}
	};

	const requestRanges = async(metricId:number) => {

		const dataController = await RCCServicesApi.getRequest({
			method: `${controllerUrl}ListRanges`,
			urlParameters: {
				taa006Id:metricId
			}
		});

		append(dataController.data);

	};

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedMetric({} as IMetric); 
		setIsAdd(true); 
		append([
			{taa007Cor:"#D91E18", taa007Valorini:0, taa007Valorfim:80.99},
			{taa007Cor:"#F5AB35", taa007Valorini:81, taa007Valorfim:99.99 },
			{taa007Cor:"#019875", taa007Valorini:100, taa007Valorfim:9999 }
		]);

		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IMetric) => {
		resetAddOrChange(); 
		setSelectedMetric(row); 
		setIsAdd(false); 
		requestRanges(row.taa006Id);
		modalAddOrChange.current?.openModal(); 
	};

	return (
		<>	{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar métrica") : translate("Alterar métrica"),
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs
					name="metricsTab"
					tabInit={0}
					options={[{
						label: translate("Dados"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
									justify="flex-end"
								>
									<RCCGrid item>
										<Audit data={selectedMetric} type="pop" position="left"/>
									</RCCGrid>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={registerAddOrChange}
										name="taa006Titulo"
										label={translate("Título")}
										maxLength={60}
										defaultValue={selectedMetric.taa006Titulo}
										error={errorsAddOrChange.taa006Titulo}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCSwitch 
										name="taa006Situacao" 
										label={translate("Ativo")} 
										register={registerAddOrChange} 
										defaultChecked ={selectedMetric.taa006Situacao !== "I"}
									/>
								</RCCGrid>
							</RCCGrid>
						)
					},
					{
						label: translate("Faixas"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<MetricsRanges
										names ={{
											table:"tatba007",
											idName: "taa007Id",
											initValueName:"taa007Valorini",
											endValueName:"taa007Valorfim",
											colorName:"taa007Cor"
										}}
										source = {fields}
										control={controlAddOrChange}
										remove={remove}
										append={append}
										errors={errorsAddOrChange}
										clearErrors={clearErrorsAddOrChange}
									/>
								</RCCGrid>
							</RCCGrid>
						)
					}
					]}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(Modals);

